// src/components/admin/ManageSupplements.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  BookOpen,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Eye,
  Download,
  Edit,
  Trash2,
  X,
  CheckCircle,
  AlertCircle,
  Calendar,
  Users,
  FileText,
  Globe,
  Share2,
  Copy,
  Twitter,
  Facebook,
  Linkedin,
  MessageCircle,
  Link as LinkIcon,
  Plus,
  RefreshCw,
  Grid,
  List,
  Inbox,
  Clock,
  BookMarked,
  User,
  Mail,
  Tag,
  Paperclip,
  AlertTriangle,
  Save,
  Image,
  Hash,
  File,
  Link2,
  CheckSquare,
  Lock,
  ArrowLeft,
  ArrowRight,
  Check,
  Upload,
  Languages
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import { EIC_ADMIN_ROLES } from './utils/AllowedRoles';
import Unauthorized from './UnauthorizedAccess';
import RichTextEditor from '../common/RichTextEditor';
import uploadEditorImage from '../../utils/uploadEditorImage';
import RichTextViewer from '../common/RichTextViewer';
import { articleTypes } from '../../types/GlobalVariables';
import { showToast } from '../../utils/toast';
// Helper function to extract plain text from HTML for word count
const getPlainTextFromHtml = (html) => {
  if (!html) return '';
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

// Count words in HTML content
const countWords = (html) => {
  const text = getPlainTextFromHtml(html);
  return text.trim() ? text.trim().split(/\s+/).length : 0;
};

// Get content preview
const getContentPreview = (html, limit = 150) => {
  if (!html) return '';
  const text = getPlainTextFromHtml(html);
  if (text.length > limit) {
    return text.substring(0, limit) + '...';
  }
  return text;
};

// Abstract Editor Component with RichTextEditor
const AbstractEditor = React.memo(({ value, onChange, onWordCountChange, error, label, required = false }) => {
  const handleChange = useCallback((content) => {
    onChange(content);
  }, [onChange]);

  const handleWordCount = useCallback((content) => {
    const words = countWords(content);
    onWordCountChange?.(words);
  }, [onWordCountChange]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <RichTextEditor
        value={value || ''}
        onChange={handleChange}
        onWordCountChange={handleWordCount}
        onImageExtracted={uploadEditorImage}
        placeholder={`Enter ${label.toLowerCase()} here...`}
        minHeight="250px"
        maxHeight="400px"
        showWordCount={true}
        wordLimit={20000}
        toolbarOptions="full"
      />

      {error && (
        <p className="mt-2 text-xs text-red-600">{error}</p>
      )}
    </div>
  );
});

// Manuscript Editor Component with RichTextEditor
const ManuscriptEditor = React.memo(({ value, onChange, onWordCountChange }) => {
  const handleChange = useCallback((content) => {
    onChange(content);
  }, [onChange]);

  const handleWordCount = useCallback((content) => {
    const words = countWords(content);
    onWordCountChange?.(words);
  }, [onWordCountChange]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Manuscript Contents / Full Text
      </label>

      <RichTextEditor
        value={value || ''}
        onChange={handleChange}
        onWordCountChange={handleWordCount}
        onImageExtracted={uploadEditorImage}
        placeholder="Enter manuscript contents here..."
        minHeight="350px"
        maxHeight="500px"
        showWordCount={true}
        toolbarOptions="full"
      />
    </div>
  );
});

const ManageSupplements = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  
  // Authorization check
  const isAuthorized = user && EIC_ADMIN_ROLES.includes(user.editorial_level || user.editorialLevel);

  if (!isAuthorized) {
    return <Unauthorized message="You need editor-in-chief or admin privileges to access this feature." />;
  }

  // Tab and Search States
  const queryParams = new URLSearchParams(location.search);
  const validTabs = ['supplements', 'issues'];
  const tabFromUrl = validTabs.includes(queryParams.get('tab')) ? queryParams.get('tab') : 'supplements';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterAuthor, setFilterAuthor] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [filterSpecialIssue, setFilterSpecialIssue] = useState('all');
  const [viewType, setViewType] = useState(() => localStorage.getItem('supplements_view') || 'grid');
  
  // Data States
  const [loading, setLoading] = useState(true);
    const [deleteLoading, setDeleteLoading] = useState(false);

  const [supplements, setSupplements] = useState([]);
  const [issues, setIssues] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalSupplements, setTotalSupplements] = useState(0);
  const [totalIssues, setTotalIssues] = useState(0);
  const [supplementsTotalPages, setSupplementsTotalPages] = useState(1);
  const [issuesTotalPages, setIssuesTotalPages] = useState(1);
  const [authors, setAuthors] = useState([]);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Modal & Form States
  const [showViewModal, setShowViewModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;
// Inside your component where selectedArticle is displayed
const [activeAbstractTab, setActiveAbstractTab] = useState('english');

// Check which abstracts have content
const hasFrenchAbstract = selectedArticle?.abstract_fr && selectedArticle.abstract_fr.trim() !== '';
const hasArabicAbstract = selectedArticle?.abstract_ar && selectedArticle.abstract_ar.trim() !== '';
const hasPortugueseAbstract = selectedArticle?.abstract_ptg && selectedArticle.abstract_ptg.trim() !== '';

// Define tabs array
const abstractTabs = [
  { id: 'english', label: 'English', hasContent: true, content: selectedArticle?.abstract || selectedArticle?.description },
  { id: 'french', label: 'French', hasContent: hasFrenchAbstract, content: selectedArticle?.abstract_fr },
  { id: 'arabic', label: 'Arabic', hasContent: hasArabicAbstract, content: selectedArticle?.abstract_ar },
  { id: 'portuguese', label: 'Portuguese', hasContent: hasPortugueseAbstract, content: selectedArticle?.abstract_ptg }
].filter(tab => tab.hasContent);


const [activeEditAbstractTab, setActiveEditAbstractTab] = useState('english');

const [editFormData, setEditFormData] = useState({
  article_type: '',
  title: '',
  authorsArray: '',
  corresponding_author: '',
  abstract: '',
  abstract_fr: '',
  abstract_ar: '',
  abstract_ptg: '',
  manuscript_contents: '',
  manuscriptCover: null,
  manuscript_file: null,
  Hyperlink: '',
  issue_number: '',
  page_number: '',
  doi_number: '',
  date_submitted: '',
  date_reviewed: '',
  date_accepted: '',
  date_published: '',
  editor_choice: false,
  open_access: false,
  is_special_issue: false,
  special_issue_id: '',
  custom_special_issue: '',
  keywords: '',
  remove_cover: false,
  remove_manuscript: false
});

  const [abstractWordCount, setAbstractWordCount] = useState(0);
  const [contentWordCount, setContentWordCount] = useState(0);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [manuscriptFilePreview, setManuscriptFilePreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [editSuccessMessage, setEditSuccessMessage] = useState('');
  const [specialIssues, setSpecialIssues] = useState([]);
  const [showCustomSpecialInput, setShowCustomSpecialInput] = useState(false);
  const [customSpecialName, setCustomSpecialName] = useState('');
  const [citationCounts, setCitationCounts] = useState({});
  const [citationLoading, setCitationLoading] = useState({});
  const [citationList, setCitationList] = useState([]);
  const [showCitationList, setShowCitationList] = useState(false);
  const [citationListArticle, setCitationListArticle] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Stats — use DB total counts (unaffected by filters/pagination)
  const stats = {
    totalSupplements: totalSupplements,
    totalIssues: totalIssues,
    totalArticles: supplements.reduce((sum, item) => sum + (item.authors ? (Array.isArray(item.authors) ? item.authors.length : 1) : 0), 0),
    totalViews: supplements.reduce((sum, item) => sum + (item.views || 0), 0),
    totalDownloads: supplements.reduce((sum, item) => sum + (item.downloads || 0), 0)
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Prefetch issues on mount so count is ready before tab is clicked
  useEffect(() => {
    const prefetchIssues = async () => {
      try {
        const response = await axios.get('/publications/issues/all', {
          params: { page: 1, limit: itemsPerPage, search: '' }
        });
        if (response.data.status === 'success') {
          const processed = response.data.articlesList.map(item => ({
            ...item,
            descriptionPreview: getContentPreview(item.description || item.abstract, 150)
          }));
          setIssues(processed);
          setIssuesTotalPages(response.data.totalPages);
          setTotalIssues(response.data.totalCount || processed.length);
        }
      } catch (err) {
        // Silently fail — will retry when tab is clicked
      }
    };
    prefetchIssues();
  }, []); // only on mount

  // Fetch Supplements
  useEffect(() => {
    const fetchSupplements = async () => {
      if (activeTab !== 'supplements') return;
      setLoading(true);
      try {
        const response = await axios.get('/publications/supplements/all', {
          params: { page: currentPage, limit: itemsPerPage, search: debouncedSearchTerm }
        });
        if (response.data.status === 'success') {
          const processed = response.data.articlesList.map(item => ({
            ...item,
            abstractPreview: getContentPreview(item.abstract || item.description, 150),
            manuscriptPreview: getContentPreview(item.manuscript_contents, 150)
          }));
          setSupplements(processed);
          setSupplementsTotalPages(response.data.totalPages);
          setTotalSupplements(response.data.totalCount || processed.length);
          
          // Extract authors for filter
          const allAuthors = response.data.articlesList.flatMap(item => {
            if (item.authors) {
              if (Array.isArray(item.authors)) {
                return item.authors.map(a => typeof a === 'object' ? (a.fullname || a.name) : a);
              }
              return [item.authors];
            }
            return [];
          }).filter(Boolean);
          setAuthors([...new Set(allAuthors)]);
        }
      } catch (err) {
        console.error(err);
        showToast('Failed to load supplements', 'error');
      } finally { setLoading(false); }
    };
    fetchSupplements();
  }, [activeTab, currentPage, debouncedSearchTerm, itemsPerPage]);

  // Fetch Issues
  useEffect(() => {
    const fetchIssues = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/publications/issues/all', {
          params: { page: currentPage, limit: itemsPerPage, search: debouncedSearchTerm, special_issue: filterSpecialIssue !== 'all' ? filterSpecialIssue : undefined }
        });
        if (response.data.status === 'success') {
          const processed = response.data.articlesList.map(item => ({
            ...item,
            descriptionPreview: getContentPreview(item.description || item.abstract, 150)
          }));
          setIssues(processed);
          setIssuesTotalPages(response.data.totalPages);
          setTotalIssues(response.data.totalCount || processed.length);
        }
      } catch (err) {
        console.error(err);
        showToast('Failed to load issues', 'error');
      } finally { setLoading(false); }
    };
    fetchIssues();
  }, [activeTab, currentPage, debouncedSearchTerm, itemsPerPage, filterSpecialIssue, refreshCounter]);

  // Update URL when tab changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('tab', activeTab);
    navigate({ search: params.toString() }, { replace: true });
    setCurrentPage(1);
    setSearchTerm('');
    setDebouncedSearchTerm('');
  }, [activeTab, navigate, location.search]);

  // Save view type to localStorage
  useEffect(() => {
    localStorage.setItem('supplements_view', viewType);
  }, [viewType]);

  // Fetch citation counts for all displayed articles with DOIs
  const fetchCitationCountForArticle = useCallback(async (article) => {
    const doi = article.doi || article.doi_number;
    if (!doi || citationLoading[article.id]) return;
    setCitationLoading(prev => ({ ...prev, [article.id]: true }));
    const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:31000';
    try {
      const response = await fetch(`${baseUrl}/journal/public/fetch-citations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doi_number: doi })
      });
      const data = await response.json();
      const count = data.success && data.data ? (data.data.total_citations || 0) : 0;
      setCitationCounts(prev => ({ ...prev, [article.id]: count }));
    } catch {
      setCitationCounts(prev => ({ ...prev, [article.id]: 0 }));
    } finally {
      setCitationLoading(prev => ({ ...prev, [article.id]: false }));
    }
  }, [citationLoading]);

  const handleViewCitationList = async (article) => {
    setCitationListArticle(article);
    setCitationList([]);
    setShowCitationList(true);
    const doi = article.doi || article.doi_number;
    if (!doi) return;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:31000';
    try {
      const response = await fetch(`${baseUrl}/journal/public/fetch-citations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doi_number: doi })
      });
      const data = await response.json();
      if (data.success && data.data) {
        setCitationList(data.data.citations || []);
      }
    } catch {
      setCitationList([]);
    }
  };

  // Fetch special issues for dropdown
  useEffect(() => {
    const fetchSpecialIssues = async () => {
      try {
        const response = await axios.get('/publications/special-issues/all');
        if (response.data.status === 'success') {
          setSpecialIssues(response.data.specialIssues);
        }
      } catch (err) {
        console.error('Failed to load special issues', err);
      }
    };
    fetchSpecialIssues();
  }, []);

  // Filtered data
  const filteredSupplements = supplements.filter(item => {
    const matchesAuthor = filterAuthor === 'all' || (item.authors && (
      Array.isArray(item.authors) ? item.authors.some(a => 
        typeof a === 'object' ? (a.fullname || a.name)?.includes(filterAuthor) : a.includes(filterAuthor)
      ) : item.authors.includes(filterAuthor)
    ));
    const matchesType = filterType === 'all' || (item.type && item.type.toLowerCase() === filterType.toLowerCase());
    return matchesAuthor && matchesType;
  });

  const filteredIssues = issues.filter(item => {
    const matchesYear = filterYear === 'all' || (item.date && item.date.includes(filterYear));
    return matchesYear;
  });

  const totalFilteredPages = Math.ceil(filteredSupplements.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSupplements = filteredSupplements.slice(startIndex, endIndex);

  const issuesFilteredTotalPages = Math.ceil(filteredIssues.length / itemsPerPage);
  const issuesStartIndex = (currentPage - 1) * itemsPerPage;
  const issuesEndIndex = issuesStartIndex + itemsPerPage;
  const currentIssues = filteredIssues.slice(issuesStartIndex, issuesEndIndex);

  // Fetch citation counts for visible articles
  useEffect(() => {
    const visible = activeTab === 'supplements' ? currentSupplements : currentIssues;
    visible.forEach(article => {
      const doi = article.doi || article.doi_number;
      if (doi && citationCounts[article.id] === undefined && !citationLoading[article.id]) {
        fetchCitationCountForArticle(article);
      }
    });
  }, [currentSupplements, currentIssues, activeTab]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalFilteredPages) {
      setCurrentPage(page);
    }
  };

  const handleIssuesPageChange = (page) => {
    if (page >= 1 && page <= issuesFilteredTotalPages) {
      setCurrentPage(page);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    if (tab === 'issues') {
      setFilterAuthor('all');
      setFilterType('all');
    } else {
      setFilterYear('all');
      setFilterSpecialIssue('all');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFilterAuthor('all');
    setFilterType('all');
    setFilterYear('all');
    setFilterSpecialIssue('all');
    setCurrentPage(1);
  };

  // Handle Edit Click
// Handle Edit Click
const handleEditClick = (article) => {
  setSelectedArticle(article);
  
  const initialAbstract = article.abstract || article.description || '';
  
  let authorsString = '';
  if (article.authors) {
    authorsString = Array.isArray(article.authors) 
      ? article.authors.map(a => typeof a === 'object' ? (a.fullname || `${a.firstname || ''} ${a.lastname || ''}`.trim()) : a).join(', ')
      : article.authors;
  }

  setEditFormData({
    article_type: article.type || '',
    title: article.title || '',
    authorsArray: authorsString,
    corresponding_author: article.corresponding_author || article.correspondingEmail || '',
    abstract: initialAbstract,
    abstract_fr: article.abstract_fr || '',  // ADD THIS LINE
    abstract_ar: article.abstract_ar || '',  // ADD THIS LINE
    abstract_ptg: article.abstract_ptg || '', // ADD THIS LINE
    manuscript_contents: article.manuscript_contents || '',
    manuscriptCover: null,
    manuscript_file: null,
    Hyperlink: article.Hyperlink || '',
    issue_number: article.issue_number || '',
    page_number: article.page_number || '',
    doi_number: article.doi || '',
    date_submitted: article.date_submitted || '',
    date_reviewed: article.date_reviewed || '',
    date_accepted: article.date_accepted || '',
    date_published: article.date_published || '',
    editor_choice: !!article.editor_choice,
    open_access: !!(article.openAccess || article.open_access),
    is_special_issue: !!article.is_special_issue,
    special_issue_id: article.special_issue_id || '',
    custom_special_issue: '',
    keywords: Array.isArray(article.keywords) ? article.keywords.join(', ') : (article.keywords || ''),
    remove_cover: false,
    remove_manuscript: false
  });

  setCoverImagePreview(article.coverImage || null);
  setManuscriptFilePreview(article.pdfUrl ? article.pdfUrl.split('/').pop() : null);
  setAbstractWordCount(countWords(initialAbstract));
  setContentWordCount(countWords(article.manuscript_contents || ''));
  setFormErrors({});
  setCurrentStep(1);
  setEditSuccessMessage('');
  setActiveEditAbstractTab('english');  // Reset to English tab when opening edit modal
  setShowEditModal(true);
};

  const validateStep = (step) => {
    const errors = {};
    
    if (step === 1) {
      if (!editFormData.title) {
        errors.title = 'Title is required';
      }
      if (activeTab === 'supplements') {
        if (!editFormData.authorsArray) {
          errors.authorsArray = 'At least one author is required';
        }
      }
      if (editFormData.is_special_issue) {
        if (!editFormData.special_issue_id && !editFormData.custom_special_issue) {
          errors.special_issue = 'Please select or enter a special issue';
        }
      }
    }
    
    if (step === 2) {
      const wordCount = countWords(editFormData.abstract);
      if (wordCount === 0) {
        errors.abstract = 'Abstract is required';
      } else if (wordCount < 50) {
        errors.abstract = `Abstract is too short (minimum 50 words, current: ${wordCount})`;
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setEditSuccessMessage('');
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevStep = () => {
    setEditSuccessMessage('');
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

const handleEditSubmit = async (closeAfterSave = true) => {
  if (!validateStep(currentStep)) {
    return;
  }
  
  setSaveLoading(true);
  try {
    const formData = new FormData();
    
    // Add all form fields
    Object.keys(editFormData).forEach(key => {
      if (editFormData[key] !== null && editFormData[key] !== undefined) {
        // Skip file objects - we'll handle them separately
        if (key === 'manuscriptCover' || key === 'manuscript_file') {
          return;
        }
        
        // Handle boolean values - convert to strings for FormData
        if (typeof editFormData[key] === 'boolean') {
          formData.append(key, editFormData[key] ? 'true' : 'false');
        } else {
          // Handle arrays (like keywords if needed)
          if (Array.isArray(editFormData[key])) {
            formData.append(key, JSON.stringify(editFormData[key]));
          } else {
            formData.append(key, editFormData[key]);
          }
        }
      }
    });

    // Handle manuscript cover file
    if (editFormData.manuscriptCover) {
      // Check if it's a File object by looking for properties
      if (typeof editFormData.manuscriptCover === 'object' && 
          editFormData.manuscriptCover !== null &&
          typeof editFormData.manuscriptCover.name === 'string') {
        formData.append('manuscriptCover', editFormData.manuscriptCover);
      }
    }
    
    // Handle manuscript file
    if (editFormData.manuscript_file) {
      // Check if it's a File object by looking for properties
      if (typeof editFormData.manuscript_file === 'object' && 
          editFormData.manuscript_file !== null &&
          typeof editFormData.manuscript_file.name === 'string') {
        formData.append('manuscript_file', editFormData.manuscript_file);
      }
    }

    console.log('Submitting update for article:', selectedArticle.id);

    const response = await axios.post(`/publications/update/${selectedArticle.id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    if (response.data.status === 'success') {
      if (closeAfterSave) {
        showToast('Updated successfully', 'success');
        setShowEditModal(false);
        setSelectedArticle(null);
        setCoverImagePreview(null);
        setManuscriptFilePreview(null);
        setCurrentStep(1);
        setRefreshCounter(prev => prev + 1);
        
        // Refresh data
        if (activeTab === 'supplements') {
          const refreshResponse = await axios.get('/publications/supplements/all', {
            params: { page: currentPage, limit: itemsPerPage, search: debouncedSearchTerm }
          });
          if (refreshResponse.data.status === 'success') {
            const processed = refreshResponse.data.articlesList.map(item => ({
              ...item,
              abstractPreview: getContentPreview(item.abstract || item.description, 150),
              manuscriptPreview: getContentPreview(item.manuscript_contents, 150)
            }));
            setSupplements(processed);
          }
        }
      } else {
        setEditSuccessMessage('Step saved successfully');
        setTimeout(() => setEditSuccessMessage(''), 5000);
        // Advance to next step after save
        setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      }
    } else {
      showToast(response.data.message || 'Failed to update', 'error');
    }
  } catch (error) {
    console.error('Update error:', error);
    showToast(error.response?.data?.message || error.message || 'Failed to update', 'error');
  } finally {
    setSaveLoading(false);
  }
};
  const handleDeleteClick = (article) => {
    setSelectedArticle(article);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText !== selectedArticle.title) {
      showToast('Title does not match', 'error');
      return;
    }

    setDeleteLoading(true);
    try {
      const response = await axios.delete(`/publications/delete/${selectedArticle.id}`);
      if (response.data.status === 'success') {
        showToast('Deleted successfully', 'success');
        setShowDeleteModal(false);
        setRefreshCounter(prev => prev + 1);
        
        // Refresh data
        if (activeTab === 'supplements') {
          setSupplements(prev => prev.filter(item => item.id !== selectedArticle.id));
        } else {
          setIssues(prev => prev.filter(item => item.id !== selectedArticle.id));
        }
      }
    } catch (error) {
      showToast('Failed to delete', 'error');
    } finally {
      setDeleteLoading(false);
      setSelectedArticle(null);
    }
  };

  const handleView = (article) => {
    setSelectedArticle(article);
    setShowViewModal(true);
  };

  const handleShareClick = (article) => {
    setSelectedArticle(article);
    setShowShareModal(true);
  };

  const handleShare = (platform) => {
    if (!selectedArticle) return;
    const url = `https://asfirj.org/content?sid=${selectedArticle.id}`;
    const title = selectedArticle.title;
    
    let shareUrl = '';
    switch(platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`;
        break;
      case 'asfischolar':
        const asfischolarUrl = `https://asfischolar.org/share?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;
        window.open(asfischolarUrl, '_blank', 'noopener,noreferrer');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        showToast('Link copied!', 'success');
        setShowShareModal(false);
        return;
      default:
        return;
    }
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
    setShowShareModal(false);
  };

  const handleDownload = async (article) => {
    if (article.pdfUrl) {
      window.open(article.pdfUrl, '_blank');
      try {
        await axios.post(`/publications/downloads/${article.id}`);
      } catch (error) {
        console.error('Error tracking download:', error);
      }
    }
  };

  const handleViewCount = async (article) => {
    try {
      await axios.post(`/publications/views/${article.id}`);
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const handleCreateNew = () => {
    const query = activeTab === 'supplements' ? '?supplement=true' : '?issue=true';
    navigate(`/editors/admin/publish${query}`);
  };

  if (loading && supplements.length === 0 && issues.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-purple-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BookOpen className="w-8 h-8 mr-3 text-purple-600" />
            Manage Supplements & Issues
          </h1>
          <p className="text-gray-600 mt-2">Manage published supplements, issues, and articles</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Supplements</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalSupplements}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Issues</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalIssues}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <BookMarked className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          
        {/*  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Articles</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalArticles}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div> */}
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Views</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalViews}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Eye className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Downloads</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalDownloads}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <Download className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => handleTabChange('supplements')}
              className={`py-4 px-1 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'supplements'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Supplements
                <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                  {supplements.length}
                </span>
              </span>
            </button>
            
            <button
              onClick={() => handleTabChange('issues')}
              className={`py-4 px-1 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'issues'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <BookMarked className="w-4 h-4" />
                Issues
                <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                  {issues.length}
                </span>
              </span>
            </button>

          </nav>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={activeTab === 'supplements' 
                  ? "Search by title, author, or ID..." 
                  : "Search by issue title or description..."}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {activeTab === 'supplements' ? (
                <>
                  <select
                    value={filterAuthor}
                    onChange={(e) => {
                      setFilterAuthor(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Authors</option>
                    {authors.map(author => (
                      <option key={author} value={author}>{author}</option>
                    ))}
                  </select>

                  <select
                    value={filterType}
                    onChange={(e) => {
                      setFilterType(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Types</option>
                    {articleTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <select
                    value={filterYear}
                    onChange={(e) => {
                      setFilterYear(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Years</option>
                    <option value="2024">2024</option>
                    <option value="2023">2023</option>
                    <option value="2022">2022</option>
                  </select>

                  <select
                    value={filterSpecialIssue}
                    onChange={(e) => {
                      setFilterSpecialIssue(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Issues</option>
                    <option value="yes">Special Issues Only</option>
                    <option value="no">Regular Issues</option>
                  </select>
                </>
              )}

              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewType('grid')}
                  className={`p-2 rounded-md transition-all ${
                    viewType === 'grid' 
                      ? 'bg-white shadow-sm text-purple-600' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Grid View"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewType('list')}
                  className={`p-2 rounded-md transition-all ${
                    viewType === 'list' 
                      ? 'bg-white shadow-sm text-purple-600' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                  }`}
                  title="List View"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              <button
                onClick={handleCreateNew}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                New {activeTab === 'supplements' ? 'Supplement' : 'Issue'}
              </button>

              <button
                onClick={clearFilters}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Reset Filters"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {(searchTerm || filterAuthor !== 'all' || filterType !== 'all' || filterYear !== 'all' || filterSpecialIssue !== 'all') && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <p className="text-gray-600">
                Showing <span className="font-medium">
                  {activeTab === 'supplements' 
                    ? `${startIndex + 1}-${Math.min(endIndex, filteredSupplements.length)}`
                    : `${issuesStartIndex + 1}-${Math.min(issuesEndIndex, filteredIssues.length)}`
                  }
                </span> of{' '}
                <span className="font-medium">
                  {activeTab === 'supplements' ? filteredSupplements.length : filteredIssues.length}
                </span> items
              </p>
              <button
                onClick={clearFilters}
                className="text-purple-600 hover:text-purple-700 text-sm font-medium"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>

        {/* Content Display */}
        {activeTab === 'supplements' ? (
          /* Supplements Display */
          <>
            {currentSupplements.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">No supplements found</h3>
                <p className="text-gray-500">
                  {searchTerm || filterAuthor !== 'all' || filterType !== 'all'
                    ? 'Try adjusting your search or filters' 
                    : 'No supplements have been published yet.'}
                </p>
                <button
                  onClick={handleCreateNew}
                  className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Create your first supplement
                </button>
              </div>
            ) : viewType === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentSupplements.map((article) => (
                  <div key={article.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                    <div 
                      className="h-48 bg-cover bg-center"
                      style={{ 
                        backgroundImage: article.coverImage ? `url('${article.coverImage}')` : 'none',
                        backgroundColor: '#f3f4f6'
                      }}
                    >
                      {!article.coverImage && (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                      <div className="w-full h-full bg-gradient-to-r from-purple-900/70 to-transparent flex items-end p-4">
                        <span className="text-white text-xs font-medium bg-purple-600 px-2 py-1 rounded">
                          {article.type || 'Article'}
                        </span>
                        {article.is_special_issue && (
                          <span className="ml-2 text-white text-xs font-medium bg-yellow-500 px-2 py-1 rounded">
                            Special Issue
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-start gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 flex-1">{article.title}</h3>
                        {article.openAccess && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded whitespace-nowrap">
                            Open Access
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-1">
                        by {Array.isArray(article.authors) 
                          ? article.authors.map(a => typeof a === 'object' ? (a.fullname || `${a.firstname || ''} ${a.lastname || ''}`.trim()) : a).join(', ')
                          : article.authors || 'Unknown'}
                      </p>
                      <div 
                        className="text-sm text-gray-600 mb-4 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: article.abstractPreview }}
                      />
                      <div className="mb-4 space-y-1">
                        <div className="flex items-center text-xs text-gray-500">
                          <Calendar className="w-3 h-3 mr-1" />
                          {article.firstPublished || 'Not published'}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <div className="flex items-center">
                            <Eye className="w-3 h-3 mr-1" />
                            {article.views || 0} Views
                          </div>
                          <div className="flex items-center">
                            <Download className="w-3 h-3 mr-1" />
                            {article.downloads || 0} Downloads
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => { handleView(article); handleViewCount(article); }}
                          className="px-3 py-1.5 text-purple-600 border border-gray-200 text-sm font-medium rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-1"
                        >
                          <Eye className="w-4 h-4" /> View
                        </button>
                        {article.pdfUrl && (
                          <button
                            onClick={() => handleDownload(article)}
className="px-3 py-1.5 text-purple-600 border border-gray-200 text-sm font-medium rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-1"
                          >
                            <Download className="w-4 h-4" /> PDF
                          </button>
                        )}
                        <button
                          onClick={() => handleShareClick(article)}
                          className="px-3 py-1.5 text-purple-600 border border-gray-200 text-sm font-medium rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-1"
                        >
                          <Share2 className="w-4 h-4" /> Share
                        </button>
                        <button
                          onClick={() => handleViewCitationList(article)}
                          className="px-3 py-1.5 text-purple-600 border border-gray-200 text-sm font-medium rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-1"
                        >
                          <FileText className="w-4 h-4" />
                          {citationLoading[article.id] ? (
                            <span className="inline-block animate-spin rounded-full h-3 w-3 border-2 border-solid border-amber-500 border-r-transparent"></span>
                          ) : (
                            <span>{citationCounts[article.id] ?? 0}</span>
                          )}
                          <span>Citations</span>
                        </button>
                        <button
                          onClick={() => handleEditClick(article)}
                          className="px-3 py-1.5 text-purple-600 border border-gray-200 text-sm font-medium rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-1"
                        >
                          <Edit className="w-4 h-4" /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(article)}
                          className="px-3 py-1.5 text-red-600 border border-gray-200 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center gap-1"
                        >
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-purple-600 to-purple-700">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-purple uppercase tracking-wider">Title</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-purple uppercase tracking-wider">Authors</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-purple uppercase tracking-wider">Type</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-purple uppercase tracking-wider">Published</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-purple uppercase tracking-wider">Views</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-purple uppercase tracking-wider">Downloads</th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-purple uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentSupplements.map((article) => (
                        <tr key={article.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{article.title}</div>
                            <div className="text-xs text-gray-500">ID: {article.id}</div>
                            <div className="text-xs text-gray-500 mt-1 line-clamp-2 max-w-xs">{article.abstractPreview}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-600 line-clamp-2 max-w-xs">
                              {Array.isArray(article.authors) 
                                ? article.authors.map(a => typeof a === 'object' ? (a.fullname || `${a.firstname || ''} ${a.lastname || ''}`.trim()) : a).join(', ')
                                : article.authors || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {article.type || '-'}
                            {article.is_special_issue && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded">
                                Special Issue
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {article.firstPublished || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {article.views || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {article.downloads || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => { handleView(article); handleViewCount(article); }}
                                className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {article.pdfUrl && (
                                <button
                                  onClick={() => handleDownload(article)}
                                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-purple-50 rounded-lg transition-colors"
                                  title="Download PDF"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleShareClick(article)}
                                className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Share"
                              >
                                <Share2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleViewCitationList(article)}
                                className="p-2 text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors relative"
                                title="Citations"
                              >
                                <FileText className="w-4 h-4" />
                                {citationLoading[article.id] ? (
                                  <span className="absolute -top-1 -right-1 inline-block animate-spin rounded-full h-3 w-3 border-2 border-solid border-amber-500 border-r-transparent"></span>
                                ) : (
                                  <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
                                    {citationCounts[article.id] ?? 0}
                                  </span>
                                )}
                              </button>
                              <button
                                onClick={() => handleEditClick(article)}
                                className="p-2 text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(article)}
                                className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {totalFilteredPages > 1 && (
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="text-sm text-gray-700">
                  Page {currentPage} of {totalFilteredPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" /> Previous
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalFilteredPages) }, (_, i) => {
                      let pageNum;
                      if (totalFilteredPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalFilteredPages - 2) pageNum = totalFilteredPages - 4 + i;
                      else pageNum = currentPage - 2 + i;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded-lg text-sm transition-colors ${
                            currentPage === pageNum
                              ? 'bg-purple-600 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalFilteredPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Issues Display */
          <>
            {currentIssues.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">No issues found</h3>
                <p className="text-gray-500">
                  {searchTerm || filterYear !== 'all' ? 'Try adjusting your search or filters' : 'No issues have been published yet.'}
                </p>
              </div>
            ) : viewType === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentIssues.map((issue) => (
                  <div key={issue.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                    <div 
                      className="h-48 bg-cover bg-center"
                      style={{ 
                        backgroundImage: issue.coverImage ? `url('${issue.coverImage}')` : 'none',
                        backgroundColor: '#f3f4f6'
                      }}
                    >
                      {!issue.coverImage && (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookMarked className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-start gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 flex-1">{issue.title}</h3>
                        {issue.is_special_issue && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded whitespace-nowrap">
                            Special Issue
                          </span>
                        )}
                      </div>
                      <div 
                        className="text-sm text-gray-600 mb-4 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: issue.descriptionPreview }}
                      />
                      <div className="mb-4 space-y-1">
                        <div className="flex items-center text-xs text-gray-500">
                          <FileText className="w-3 h-3 mr-1" />
                          {issue.articles || 0} Articles
                        </div>
                        <div className="flex items-center text-xs text-gray-500">
                          <Calendar className="w-3 h-3 mr-1" />
                          {issue.date || 'Not dated'}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => handleView(issue)}
                          className="px-3 py-2 text-purple-600 border border-gray-200 text-sm font-medium rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
                        >
                          <Eye className="w-4 h-4" /> View
                        </button>
                        <button
                          onClick={() => handleViewCitationList(issue)}
                          className="px-3 py-2 text-purple-600 border border-gray-200 text-sm font-medium rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
                        >
                          <FileText className="w-4 h-4" />
                          {citationLoading[issue.id] ? (
                            <span className="inline-block animate-spin rounded-full h-3 w-3 border-2 border-solid border-amber-500 border-r-transparent"></span>
                          ) : (
                            <span>{citationCounts[issue.id] ?? 0}</span>
                          )}
                          <span>Citations</span>
                        </button>
                        <button
                          onClick={() => handleEditClick(issue)}
                          className="px-3 py-2 text-purple-600 border border-gray-200 text-sm font-medium rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
                        >
                          <Edit className="w-4 h-4" /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(issue)}
                          className="px-3 py-2 text-red-600 border border-gray-200 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-purple-600 to-purple-700">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-purple uppercase tracking-wider">Issue</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-purple uppercase tracking-wider">Description</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-purple uppercase tracking-wider">Articles</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-purple uppercase tracking-wider">Date</th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-purple uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentIssues.map((issue) => (
                        <tr key={issue.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{issue.title}</div>
                            <div className="text-xs text-gray-500">ID: {issue.id}</div>
                            {issue.is_special_issue && (
                              <span className="mt-1 inline-flex items-center px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded">
                                Special Issue
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-600 line-clamp-2 max-w-xs">{issue.descriptionPreview}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{issue.articles || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{issue.date || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleView(issue)}
                                className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleViewCitationList(issue)}
                                className="p-2 text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors relative"
                                title="Citations"
                              >
                                <FileText className="w-4 h-4" />
                                {citationLoading[issue.id] ? (
                                  <span className="absolute -top-1 -right-1 inline-block animate-spin rounded-full h-3 w-3 border-2 border-solid border-amber-500 border-r-transparent"></span>
                                ) : (
                                  <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
                                    {citationCounts[issue.id] ?? 0}
                                  </span>
                                )}
                              </button>
                              <button
                                onClick={() => handleEditClick(issue)}
                                className="p-2 text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(issue)}
                                className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {issuesFilteredTotalPages > 1 && (
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="text-sm text-gray-700">
                  Page {currentPage} of {issuesFilteredTotalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleIssuesPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" /> Previous
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, issuesFilteredTotalPages) }, (_, i) => {
                      let pageNum;
                      if (issuesFilteredTotalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= issuesFilteredTotalPages - 2) pageNum = issuesFilteredTotalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handleIssuesPageChange(pageNum)}
                          className={`w-8 h-8 rounded-lg text-sm transition-colors ${
                            currentPage === pageNum
                              ? 'bg-purple-600 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => handleIssuesPageChange(currentPage + 1)}
                    disabled={currentPage === issuesFilteredTotalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* View Modal */}
        {showViewModal && selectedArticle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  {activeTab === 'supplements' ? 'Article Details' : 'Issue Details'}
                </h3>
                <button
                  onClick={() => { setShowViewModal(false); setSelectedArticle(null); }}
                  className="text-gray-400 hover:text-gray-500 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                {selectedArticle.coverImage && (
                  <div className="flex justify-center">
                    <img src={selectedArticle.coverImage} alt="Cover" className="max-h-64 object-contain rounded-lg border border-gray-200" />
                  </div>
                )}
                
                <div>
                  <p className="text-sm font-medium text-purple-600 mb-1">ID: {selectedArticle.id}</p>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedArticle.title}</h2>
                  {selectedArticle.type && (
                    <p className="text-sm text-gray-500 mt-1">{selectedArticle.type}</p>
                  )}
                </div>

                <div className="flex gap-2 flex-wrap">
                  {selectedArticle.openAccess && (
                    <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                      Open Access
                    </span>
                  )}
                  {selectedArticle.editor_choice && (
                    <span className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full">
                      Editor's Choice
                    </span>
                  )}
                  {selectedArticle.is_special_issue && (
                    <span className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full">
                      Special Issue
                    </span>
                  )}
                </div>

                {selectedArticle.authors && selectedArticle.authors.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Users className="w-4 h-4 mr-2" /> Authors
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedArticle.authors.map((author, index) => (
                        <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                          {typeof author === 'object' ? (author.fullname || `${author.firstname || ''} ${author.lastname || ''}`.trim()) : author}
                        </span>
                      ))}
                    </div>
                  </div>
                )}


{(selectedArticle?.abstract || selectedArticle?.description || hasFrenchAbstract || hasArabicAbstract || hasPortugueseAbstract) && (
  <div className="mb-6">
    <div className="border-b border-gray-200">
      <nav className="flex space-x-4 overflow-x-auto" aria-label="Abstract Tabs">
        {abstractTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveAbstractTab(tab.id)}
            className={`
              px-4 py-2 text-sm font-medium rounded-t-lg transition-all duration-200
              whitespace-nowrap
              ${activeAbstractTab === tab.id
                ? 'text-[--asfi-purple] border-b-2 border-[--asfi-purple] bg-purple-50/30'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }
            `}
          >
            <div className="flex items-center gap-2">
              <Languages className="w-4 h-4" />
              <span>{tab.label}</span>
              {/* {tab.id === 'english' && (
                // <span className="text-red-500 text-xs ml-1">*</span>
              )} */}
            </div>
          </button>
        ))}
      </nav>
    </div>

    <div className="mt-4">
      {activeAbstractTab === 'english' && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <RichTextViewer content={selectedArticle?.abstract || selectedArticle?.description || "No abstract available."} />
        </div>
      )}
      
      {activeAbstractTab === 'french' && hasFrenchAbstract && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <RichTextViewer content={selectedArticle?.abstract_fr || "No abstract available in French."} />
        </div>
      )}
      
      {activeAbstractTab === 'arabic' && hasArabicAbstract && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <RichTextViewer content={selectedArticle?.abstract_ar || "No abstract available in Arabic."} />
        </div>
      )}
      
      {activeAbstractTab === 'portuguese' && hasPortugueseAbstract && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <RichTextViewer content={selectedArticle?.abstract_ptg || "No abstract available in Portuguese."} />
        </div>
      )}
    </div>
  </div>
)}

                {selectedArticle.manuscript_contents && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-700 mb-2">Full Text Preview</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <RichTextViewer content={selectedArticle.manuscript_contents} />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {selectedArticle.issue_number && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Issue Number</h4>
                      <p className="text-sm text-gray-600 flex items-center">
                        <Hash className="w-4 h-4 text-gray-400 mr-2" />
                        {selectedArticle.issue_number}
                      </p>
                    </div>
                  )}
                  {selectedArticle.page_number && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Page Number</h4>
                      <p className="text-sm text-gray-600 flex items-center">
                        <FileText className="w-4 h-4 text-gray-400 mr-2" />
                        {selectedArticle.page_number}
                      </p>
                    </div>
                  )}
                  {selectedArticle.doi && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">DOI</h4>
                      <p className="text-sm text-gray-600 flex items-center">
                        <Link2 className="w-4 h-4 text-gray-400 mr-2" />
                        {selectedArticle.doi}
                      </p>
                    </div>
                  )}
                  {(selectedArticle.doi || selectedArticle.doi_number) && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Citations</h4>
                      <p className="text-sm text-gray-600 flex items-center">
                        <FileText className="w-4 h-4 text-gray-400 mr-2" />
                        {citationLoading[selectedArticle.id] ? (
                          <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-solid border-amber-500 border-r-transparent"></span>
                        ) : (citationCounts[selectedArticle.id] || 0) > 0 ? (
                          <button
                            onClick={() => handleViewCitationList(selectedArticle)}
                            className="text-amber-600 hover:text-amber-700 font-medium hover:underline"
                          >
                            Cited by {citationCounts[selectedArticle.id]} {citationCounts[selectedArticle.id] === 1 ? 'article' : 'articles'}
                          </button>
                        ) : (
                          <span className="text-gray-500">No citations found</span>
                        )}
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {selectedArticle.date_published && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Date Published</h4>
                      <p className="text-sm text-gray-600 flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        {selectedArticle.date_published}
                      </p>
                    </div>
                  )}
                  {selectedArticle.firstPublished && !selectedArticle.date_published && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">First Published</h4>
                      <p className="text-sm text-gray-600 flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        {selectedArticle.firstPublished}
                      </p>
                    </div>
                  )}
                </div>

                {selectedArticle.pdfUrl && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Paperclip className="w-4 h-4 mr-2" /> Files
                    </h4>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-700">Full Text PDF</span>
                      </div>
                      <a
                        href={selectedArticle.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 text-gray-500 hover:text-purple-600 hover:bg-white rounded transition-colors"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => { setShowViewModal(false); setSelectedArticle(null); }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => { setShowViewModal(false); handleShareClick(selectedArticle); }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" /> Share
                </button>
                {selectedArticle.pdfUrl && (
                  <a
                    href={selectedArticle.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" /> Download PDF
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && selectedArticle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Share Article</h3>
           

                  <button
                    onClick={() => { setShowShareModal(false); setSelectedArticle(null); }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Article:</p>
                  <p className="text-sm text-gray-900 mt-1 line-clamp-2">{selectedArticle.title}</p>
                  <p className="text-xs text-gray-500 mt-1">ID: {selectedArticle.id}</p>
                </div>
                <div className="space-y-3">
                       <button onClick={() => handleShare('asfischolar')} className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-3 border border-gray-200"><img src="/assets/images/ASFIScholar_Logo.png" alt="" className="h-6" /><div><p className="font-medium">ASFIScholar</p><p className="text-xs text-gray-500">Share on ASFIScholar platform</p></div></button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-3 border border-gray-200 transition-colors"
                  >
                    <Twitter className="w-6 h-6 text-blue-400" />
                    <div>
                      <p className="font-medium">Twitter / X</p>
                      <p className="text-xs text-gray-500">Share on Twitter</p>
                    </div>
                  </button>
                  <button
                    onClick={() => handleShare('facebook')}
                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-3 border border-gray-200 transition-colors"
                  >
                    <Facebook className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-medium">Facebook</p>
                      <p className="text-xs text-gray-500">Share on Facebook</p>
                    </div>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-3 border border-gray-200 transition-colors"
                  >
                    <Linkedin className="w-6 h-6 text-blue-700" />
                    <div>
                      <p className="font-medium">LinkedIn</p>
                      <p className="text-xs text-gray-500">Share on LinkedIn</p>
                    </div>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-3 border border-gray-200 transition-colors"
                  >
                    <MessageCircle className="w-6 h-6 text-green-500" />
                    <div>
                      <p className="font-medium">WhatsApp</p>
                      <p className="text-xs text-gray-500">Share on WhatsApp</p>
                    </div>
                  </button>
                  <button
                    onClick={() => handleShare('email')}
                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-3 border border-gray-200 transition-colors"
                  >
                    <Mail className="w-6 h-6 text-gray-600" />
                    <div>
                      <p className="font-medium">Email</p>
                      <p className="text-xs text-gray-500">Share via Email</p>
                    </div>
                  </button>
                  <div className="border-t border-gray-200 my-2"></div>
                  <button
                    onClick={() => handleShare('copy')}
                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-3 border border-gray-200 transition-colors"
                  >
                    <Copy className="w-6 h-6 text-gray-600" />
                    <div>
                      <p className="font-medium">Copy Link</p>
                      <p className="text-xs text-gray-500">Copy article URL to clipboard</p>
                    </div>
                  </button>
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => { setShowShareModal(false); setSelectedArticle(null); }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Citation List Modal */}
        {showCitationList && citationListArticle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-600" />
                  Citing Articles
                </h3>
                <button
                  onClick={() => { setShowCitationList(false); setCitationListArticle(null); }}
                  className="text-gray-400 hover:text-gray-500 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Article:</p>
                  <p className="text-sm text-gray-900 mt-1">{citationListArticle.title}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Cited by <span className="font-semibold text-amber-600">{citationCounts[citationListArticle.id] || 0}</span> {citationCounts[citationListArticle.id] === 1 ? 'article' : 'articles'}
                  </p>
                </div>

                {!citationListArticle.doi && !citationListArticle.doi_number ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium text-gray-700 mb-1">No DOI assigned</p>
                    <p className="text-sm">This article does not have a DOI number, so citation tracking is unavailable.</p>
                  </div>
                ) : citationList.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No citation details available</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {citationList.map((citation, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-bold">
                            {index + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            {citation.citing_doi && (
                              <div className="mb-1">
                                <span className="text-xs font-medium text-gray-500">Citing DOI:</span>
                                <a
                                  href={`https://doi.org/${citation.citing_doi}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-1 text-sm text-blue-600 hover:underline break-all"
                                >
                                  {citation.citing_doi}
                                </a>
                              </div>
                            )}
                            {citation.publication_date && (
                              <div className="text-xs text-gray-500">
                                <span className="font-medium">Date:</span> {citation.publication_date}
                              </div>
                            )}
                            {citation.timespan && (
                              <div className="text-xs text-gray-500">
                                <span className="font-medium">Timespan:</span> {citation.timespan}
                              </div>
                            )}
                            {citation.journal_self_citation === 'true' && (
                              <span className="inline-flex items-center px-2 py-0.5 mt-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded">
                                Journal Self-Citation
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end">
                <button
                  onClick={() => { setShowCitationList(false); }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
{showEditModal && selectedArticle && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Edit {activeTab === 'supplements' ? 'Article' : 'Issue'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Step {currentStep} of {totalSteps}: {currentStep === 1 ? 'Basic Information' : currentStep === 2 ? 'Abstract' : currentStep === 3 ? 'Manuscript Content' : currentStep === 4 ? 'Publication Details' : 'Dates & Verification'}
            </p>
          </div>
          <button
            onClick={() => { setShowEditModal(false); setSelectedArticle(null); setCoverImagePreview(null); setManuscriptFilePreview(null); setCurrentStep(1); setAbstractWordCount(0); setContentWordCount(0); }}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step < currentStep ? 'bg-green-500 text-white' :
                    step === currentStep ? 'bg-purple-600 text-white' :
                    'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step < currentStep ? <Check className="w-4 h-4" /> : step}
                </div>
                {step < totalSteps && (
                  <div className={`w-16 h-1 mx-2 ${step < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>Basic Info</span>
            <span>Abstract</span>
            <span>Content</span>
            <span>Details</span>
            <span>Dates</span>
          </div>
        </div>
      </div>

      {/* REMOVED the form onSubmit - using separate button handlers instead */}
      <div>
        <div className="p-6 min-h-[400px]">
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({...editFormData, title: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    formErrors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter title..."
                />
                {formErrors.title && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Article Type
                </label>
                <select
                  value={editFormData.article_type}
                  onChange={(e) => setEditFormData({...editFormData, article_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select Type</option>
                  {articleTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {activeTab === 'supplements' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Author(s) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editFormData.authorsArray}
                      onChange={(e) => setEditFormData({...editFormData, authorsArray: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        formErrors.authorsArray ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter authors separated by commas..."
                    />
                    {formErrors.authorsArray && (
                      <p className="mt-1 text-xs text-red-600">{formErrors.authorsArray}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Corresponding Author's Email
                    </label>
                    <input
                      type="email"
                      value={editFormData.corresponding_author}
                      onChange={(e) => setEditFormData({...editFormData, corresponding_author: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="corresponding@email.com"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Issue
                </label>
                <div className="flex items-center gap-3 mb-3">
                  <span className={`text-sm font-medium ${!editFormData.is_special_issue ? 'text-purple-700' : 'text-gray-400'}`}>No</span>
                  <button
                    type="button"
                    onClick={() => {
                      setEditFormData({...editFormData, is_special_issue: !editFormData.is_special_issue, special_issue_id: '', custom_special_issue: ''});
                      setShowCustomSpecialInput(false);
                      setCustomSpecialName('');
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${editFormData.is_special_issue ? 'bg-purple-600' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editFormData.is_special_issue ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <span className={`text-sm font-medium ${editFormData.is_special_issue ? 'text-purple-700' : 'text-gray-400'}`}>Yes</span>
                </div>

                {formErrors.special_issue && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.special_issue}</p>
                )}
                {editFormData.is_special_issue && (
                  <div>
                    {!showCustomSpecialInput ? (
                      <select
                        value={editFormData.special_issue_id}
                        onChange={(e) => {
                          if (e.target.value === '__other__') {
                            setShowCustomSpecialInput(true);
                            setEditFormData({...editFormData, special_issue_id: ''});
                          } else {
                            setEditFormData({...editFormData, special_issue_id: e.target.value, custom_special_issue: ''});
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      >
                        <option value="">Select Special Issue...</option>
                        {specialIssues.map(si => (
                          <option key={si.special_issue_id} value={si.special_issue_id}>
                            {si.special_issue_name}
                          </option>
                        ))}
                        <option value="__other__">Other (type custom)</option>
                      </select>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={customSpecialName}
                          onChange={(e) => {
                            setCustomSpecialName(e.target.value);
                            setEditFormData({...editFormData, custom_special_issue: e.target.value});
                          }}
                          placeholder="Enter new special issue name..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => { setShowCustomSpecialInput(false); setCustomSpecialName(''); setEditFormData({...editFormData, custom_special_issue: ''}); }}
                          className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-4 overflow-x-auto" aria-label="Abstract Tabs">
                  {[
                    { id: 'english', label: 'English', required: true, value: editFormData.abstract, onChange: (val) => setEditFormData({...editFormData, abstract: val}), wordCount: abstractWordCount, setWordCount: setAbstractWordCount, error: formErrors.abstract },
                    { id: 'french', label: 'French', required: false, value: editFormData.abstract_fr, onChange: (val) => setEditFormData({...editFormData, abstract_fr: val}), wordCount: null, setWordCount: null, error: formErrors.abstract_fr },
                    { id: 'arabic', label: 'Arabic', required: false, value: editFormData.abstract_ar, onChange: (val) => setEditFormData({...editFormData, abstract_ar: val}), wordCount: null, setWordCount: null, error: formErrors.abstract_ar },
                    { id: 'portuguese', label: 'Portuguese', required: false, value: editFormData.abstract_ptg, onChange: (val) => setEditFormData({...editFormData, abstract_ptg: val}), wordCount: null, setWordCount: null, error: formErrors.abstract_ptg }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveEditAbstractTab(tab.id)}
                      className={`
                        px-4 py-2 text-sm font-medium rounded-t-lg transition-all duration-200
                        whitespace-nowrap
                        ${activeEditAbstractTab === tab.id
                          ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/30'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }
                      `}
                    >
                      <div className="flex items-center gap-2">
                        <span>{tab.label}</span>
                        {tab.required && (
                          <span className="text-red-500 text-xs ml-1">*</span>
                        )}
                      </div>
                    </button>
                  ))}
                </nav>
              </div>

              <div className="mt-4">
                {activeEditAbstractTab === 'english' && (
                  <AbstractEditor
                    value={editFormData.abstract}
                    onChange={(val) => setEditFormData({...editFormData, abstract: val})}
                    onWordCountChange={setAbstractWordCount}
                    error={formErrors.abstract}
                    label="English Abstract"
                    required={true}
                  />
                )}
                {activeEditAbstractTab === 'french' && (
                  <AbstractEditor
                    value={editFormData.abstract_fr}
                    onChange={(val) => setEditFormData({...editFormData, abstract_fr: val})}
                    error={formErrors.abstract_fr}
                    label="French Abstract"
                    required={false}
                  />
                )}
                {activeEditAbstractTab === 'arabic' && (
                  <AbstractEditor
                    value={editFormData.abstract_ar}
                    onChange={(val) => setEditFormData({...editFormData, abstract_ar: val})}
                    error={formErrors.abstract_ar}
                    label="Arabic Abstract"
                    required={false}
                  />
                )}
                {activeEditAbstractTab === 'portuguese' && (
                  <AbstractEditor
                    value={editFormData.abstract_ptg}
                    onChange={(val) => setEditFormData({...editFormData, abstract_ptg: val})}
                    error={formErrors.abstract_ptg}
                    label="Portuguese Abstract"
                    required={false}
                  />
                )}
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <ManuscriptEditor
              value={editFormData.manuscript_contents}
              onChange={(val) => setEditFormData({...editFormData, manuscript_contents: val})}
              onWordCountChange={setContentWordCount}
            />
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-600 mb-2">Cover Image</p>
                  <input
                    type="file"
                    id="coverUpload"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setEditFormData({...editFormData, manuscriptCover: file});
                        setCoverImagePreview(URL.createObjectURL(file));
                      }
                    }}
                  />
                  <label
                    htmlFor="coverUpload"
                    className="inline-block px-4 py-2 bg-purple-600 text-white text-sm rounded-lg cursor-pointer hover:bg-purple-700 transition-colors"
                  >
                    Choose file
                  </label>
                  {coverImagePreview && (
                    <div className="mt-4">
                      <img src={coverImagePreview} className="h-32 mx-auto rounded-lg" alt="Preview" />
                      <button
                        type="button"
                        onClick={() => {
                          setCoverImagePreview(null);
                          setEditFormData({...editFormData, manuscriptCover: null, remove_cover: true});
                        }}
                        className="mt-2 text-xs text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                  <FileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-600 mb-2">Manuscript PDF</p>
                  <input
                    type="file"
                    id="pdfUpload"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setEditFormData({...editFormData, manuscript_file: file});
                        setManuscriptFilePreview(file.name);
                      }
                    }}
                  />
                  <label
                    htmlFor="pdfUpload"
                    className="inline-block px-4 py-2 bg-purple-600 text-white text-sm rounded-lg cursor-pointer hover:bg-purple-700 transition-colors"
                  >
                    Choose file
                  </label>
                  {manuscriptFilePreview && (
                    <div className="mt-4">
                      <p className="text-xs text-gray-600">{manuscriptFilePreview}</p>
                      <button
                        type="button"
                        onClick={() => {
                          setManuscriptFilePreview(null);
                          setEditFormData({...editFormData, manuscript_file: null, remove_manuscript: true});
                        }}
                        className="mt-1 text-xs text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  DOI Number
                </label>
                <input
                  type="text"
                  value={editFormData.doi_number}
                  onChange={(e) => setEditFormData({...editFormData, doi_number: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="10.xxxx/xxxxx"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Issue Number
                </label>
                <input
                  type="text"
                  value={editFormData.issue_number}
                  onChange={(e) => setEditFormData({...editFormData, issue_number: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Volume 1, Issue 1"
                />
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date Published
                  </label>
                  <input
                    type="date"
                    value={editFormData.date_published}
                    onChange={(e) => setEditFormData({...editFormData, date_published: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Keywords (comma separated)
                  </label>
                  <input
                    type="text"
                    value={editFormData.keywords}
                    onChange={(e) => setEditFormData({...editFormData, keywords: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="keyword1, keyword2, keyword3"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editFormData.open_access}
                    onChange={(e) => setEditFormData({...editFormData, open_access: e.target.checked})}
                    className="w-4 h-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Open Access</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editFormData.editor_choice}
                    onChange={(e) => setEditFormData({...editFormData, editor_choice: e.target.checked})}
                    className="w-4 h-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Editor's Choice</span>
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-between rounded-b-xl">
          <div className="flex gap-2">
            {editSuccessMessage && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg">
                <CheckCircle className="w-4 h-4" />
                {editSuccessMessage}
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handlePrevStep}
              disabled={currentStep === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" /> Previous
            </button>
            <button
              type="button"
              onClick={() => { setShowEditModal(false); setSelectedArticle(null); setCoverImagePreview(null); setManuscriptFilePreview(null); setCurrentStep(1); setEditSuccessMessage(''); }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleEditSubmit(false)}
              disabled={saveLoading}
              className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-700"></div>
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save & Continue
            </button>
            <button
              type="button"
              onClick={() => handleEditSubmit(true)}
              disabled={saveLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" /> Save & Close
                </>
              )}
            </button>
            {currentStep < totalSteps && (
              <button
                type="button"
                onClick={handleNextStep}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                Next <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
)}
        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedArticle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Delete {activeTab === 'supplements' ? 'Article' : 'Issue'}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">This action cannot be undone.</p>
                  </div>
                </div>
                
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Title:</p>
                  <p className="text-sm text-gray-900 mt-1">{selectedArticle.title}</p>
                  <p className="text-xs text-gray-500 mt-1">ID: {selectedArticle.id}</p>
                </div>
                
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 flex items-start">
                    <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Warning:</strong> This will permanently delete this {activeTab === 'supplements' ? 'article' : 'issue'} and all associated data. This action cannot be reversed.
                    </span>
                  </p>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="deleteConfirm" className="block text-sm font-medium text-gray-700 mb-1">
                    Type the title to confirm deletion
                  </label>
                  <input
                    type="text"
                    id="deleteConfirm"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Enter the exact title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
                
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => { setShowDeleteModal(false); setSelectedArticle(null); setDeleteConfirmText(''); }}
                    disabled={deleteLoading}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={deleteLoading || deleteConfirmText !== selectedArticle.title}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleteLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" /> Permanently Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="text-center py-6 border-t border-gray-200 mt-8">
          <p className="text-sm text-gray-500">© {new Date().getFullYear()} ASFI - All rights reserved</p>
        </footer>
      </div>
    </div>
  );
};

export default ManageSupplements;